import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import ContactInfoForm from "../doctor/components/ContactInfoForm";
import Tooltip from "@mui/material/Tooltip";
import { visuallyHidden } from "@mui/utils";
import { convertToUSFormat } from "../../helpers/utils";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import PatientIdModal from "../doctor/components/PatientIdModal";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import CloseIcon from "@mui/icons-material/Close";
import useHeaders from "../../helpers/useHeaders";
import useEncryption from "../../services/useEncryption";
import { updateLeadStatusApi } from "../../api/userApi/userApi";
import { getAllLeadsAPI } from "../../api/adminApi/adminApi";
import { toast } from "react-toastify";
import useToast from "../../helpers/useToast";
import { getAgeCategory } from "../../helpers/utils";
import { getTopSymptoms } from "../../helpers/utils";
import AddNotesModal from "../doctor/components/AddNotesModal";
import Notes from "../doctor/components/Notes";
import {
  above13AgeQuestionsBVDQ,
  age4to8QuestionsBVDQ,
  age9to13QuestionsBVDQ,
  age4to8QuestionsHistory,
  above13AgeQuestionsHistory,
  age9to13QuestionsHistory,
} from "../../helpers/questions";
import ChangeProviderModal from "./ChangeProviderModal";
import { useDispatch } from "react-redux";
import { setLeadPageRefresh, setSelectedId } from "../../redux/slice/authSlice";
import { createAuditLogs } from "../../api/providerApi/providerApi";

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const options = { year: "numeric", month: "long", day: "numeric" };
  return date.toLocaleDateString("en-US", options);
};

const headCells = [
  {
    id: "created_at",
    numeric: true,
    disablePadding: true,
    label: "Date Submitted",
  },
  {
    id: "name",
    numeric: true,
    disablePadding: false,
    label: "Patient Name",
  },
  {
    id: "age",
    numeric: true,
    disablePadding: false,
    label: "Age",
  },
  {
    id: "phone_number ",
    numeric: true,
    disablePadding: false,
    label: "Phone",
  },
  {
    id: "bvdqScores",
    numeric: true,
    disablePadding: false,
    label: "BVDQ™ Score",
  },
  // {
  //   id: "country_of_residence",
  //   numeric: true,
  //   disablePadding: false,
  //   label: "Country",
  // },
  {
    id: "state",
    numeric: true,
    disablePadding: false,
    label: "State",
  },
  {
    id: "city",
    numeric: true,
    disablePadding: false,
    label: "City",
  },
  {
    id: "location_name",
    numeric: true,
    disablePadding: false,
    label: "Location Name",
  },
  {

    id: "status",
    numeric: true,
    disablePadding: false,
    label: "Status",
  },
  {
    id: "actions",
    numeric: true,
    disablePadding: false,
    label: "Actions",
  },
];

function EnhancedTableHead(props) {
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
  } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{
              "aria-label": "select all desserts",
            }}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            // align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

function EnhancedTableToolbar(props) {
  // const { numSelected } = props;

  return <></>;
}

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
};

export const TableLists = ({
  activeTab,
  refresh,
  search,
  providerId,
}) => {
  const { encryptData, decryptData } = useEncryption();
  const toastOptions = useToast();
  const headers = useHeaders();
  const [loading, setLoading] = React.useState(false);
  const [order, setOrder] = React.useState("desc");
  const [orderBy, setOrderBy] = React.useState("created_at");
  const [selected, setSelected] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(100);
  const [rows, setRows] = useState([]);
  const [totalLeads, setTotalLeads] = useState(0);
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [updatedNotes, setUpdatedNotes] = React.useState(false);
  const [changeProvider, setChangeProvider] = useState(false);
  const [openPatientId, setOpenPatientId] = useState(false);
  const dispatch = useDispatch();
  const [update, setUpdate] = useState(false);
  const dialogRef = useRef(null);

  const handleCopy = () => {
    if (selectedLead?.patientId) {
      navigator.clipboard.writeText(selectedLead?.patientId);
      toast.success("Patient ID copied to clipboard!");
    }
  };

  const getclassName = (status) => {
    switch (status) {
      case "New Lead":
        return "newlead";
      case "Scheduled For Contact":
        return "scheduledcontact";
      case "Contact Attempted":
        return "contacted";
      case "Ready To Schedule":
        return "readytoSchedule";
      case "In Process":
        return "inprogress";
      case "Scheduled":
        return "scheduled";
      case "UnScheduled":
        return "unscheduled";
      case "Archived":
        return "archived";
      default:
        return "";
    }
  };

  useEffect(() => {
    fetchData();
  }, [
    page,
    rowsPerPage,
    order,
    orderBy,
    activeTab,
    refresh,
    search,
    providerId,
    update,
  ]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await getAllLeadsAPI(
        page,
        rowsPerPage,
        orderBy,
        order,
        activeTab,
        search,
        providerId
      );
      const decryptedData = decryptData(response?.data?.data);
      setRows(decryptedData?.leads);

      setTotalLeads(decryptedData?.totalLeads);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching data:", error);
    }
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = rows.map((row) => row._id);
      setSelected(newSelected);
      return;
    }
    dispatch(setSelectedId(selected));
    setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    dispatch(setSelectedId(newSelected));
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // const handleChangeDense = (event) => {
  //   setDense(event.target.checked);
  // };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  // const emptyRows = rows.length == 0;

  // const [age, setAge] = React.useState("");
  // const handleChange = (event) => {
  //   setAge(event.target.value);
  // };

  const handleStatusChange = async (event, row) => {
    const newStatus = event.target.value;
    const leadId = row._id;
    const previousRows = [...rows];

    if (activeTab === "All Leads") {
      setRows((prevData) =>
        prevData.map((item) =>
          item._id === leadId ? { ...item, status: newStatus } : item
        )
      );

      try {
        const payload = {
          status: newStatus,
        };
        const data = encryptData(payload);
        const response = await updateLeadStatusApi(leadId, data, headers);
        let obj = {
          event: "SuperAdmin has updated Lead Status.",
        };
        await createAuditLogs({ data: encryptData(obj) }, headers);

        toast.success(response.data.message, toastOptions);
        dispatch(setLeadPageRefresh(!refresh));

      } catch (error) {
        setRows(previousRows);
        toast.error(error?.response?.data?.message, toastOptions);
      }
    } else {
      setRows((prevData) => prevData.filter((item) => item._id !== leadId));

      try {
        const payload = {
          status: newStatus,
        };
        const data = encryptData(payload);
        const response = await updateLeadStatusApi(leadId, data, headers);
        let obj = {
          event: "SuperAdmin has updated Lead Status.",
        };
        await createAuditLogs({ data: encryptData(obj) }, headers);

        toast.success(response.data.message, toastOptions);
      } catch (error) {
        setRows(previousRows);
        toast.error(error?.response?.data?.message, toastOptions);
      }
    }
  };

  const handleStatusUpdate = async (event) => {
    const newStatus = event.target.value;
    const leadId = selectedLead._id;
    const previousLead = { ...selectedLead };

    setSelectedLead((prevLead) => ({
      ...prevLead,
      status: newStatus,
    }));

    try {
      const payload = {
        status: newStatus,
      };
      const data = encryptData(payload);
      const response = await updateLeadStatusApi(leadId, data, headers);
      let obj = {
        event: "SuperAdmin has updated Lead Status.",
      };
      await createAuditLogs({ data: encryptData(obj) }, headers);
      toast.success(response.data.message, toastOptions);
    } catch (error) {
      setSelectedLead(previousLead);
      toast.error(error?.response?.data?.message, toastOptions);
    }
  };

  const [open, setOpen] = useState(false);

  const handleClickOpen = (leadId) => {
    setSelectedLeadId(leadId);
    setOpen(true);
  };
  const handleClose = () => {
    if (!selectedLead) {
      setSelectedLeadId(null);
    }
    setUpdatedNotes((prev) => !prev);
    setOpen(false);
    setChangeProvider(false);
  };
  const [openDialouge, setopenDialouge] = useState(false);

  const handleClickOpenedLeadModal = (leadId) => {
    const lead = rows.find((lead) => lead._id === leadId);
    setSelectedLead(lead);
    setSelectedLeadId(lead._id);
    setopenDialouge(true);
  };

  const handleClosedLeadModal = () => {
    setopenDialouge(false);
    setSelectedLead(null);
    setSelectedLeadId(null);
    // setRefresh((prev) => !prev);
  };

  const handleClickChangeProvider = (lead) => {
    setSelectedLead(lead);
    setChangeProvider(true);
  };

  const handleClickOpenPatientId = (leadId) => {
    const lead = rows.find((lead) => lead._id === leadId);
    setSelectedLead(lead);
    setOpenPatientId(true);
  };

  const handleClickPatientIdClose = () => {
    setSelectedLead(null);
    setOpenPatientId(false);
  };

  // const [profilePic, setProfilePic] = useState(null); // Initialize with null to handle no image case

  // const handleImageChange = (event) => {
  //   const file = event.target.files[0];
  //   if (file) {
  //     const reader = new FileReader();
  //     reader.onloadend = () => {
  //       setProfilePic(reader.result); // Set the image URL to the state
  //     };
  //     reader.readAsDataURL(file); // Convert the file to a data URL
  //   }
  // };

  // const triggerFileUpload = () => {
  //   document.getElementById("file-upload").click();
  // };

  const sliderBarRef = useRef(null);
  const pointerRef = useRef(null);

  const topSymptoms = getTopSymptoms(
    decryptData(selectedLead?.formsortResponse)?.answers
  );
  const answers = decryptData(selectedLead?.formsortResponse)?.answers;

  const topSymptomNames = topSymptoms.map((symptom) => symptom.name);

  const symptomKeyMap = {
    Dizziness: "dizziness",
    Nausea: "nausea",
    Anxiety: "anxiety",
    Headaches: "headache",
    "Neck Pain": "neck_pain",
    "Unsteady with Walking": "balance_issues",
    "Sensitivity to Light": "light_sensitivity",
    "Reading Difficulty": "reading_challenges",
    "Sound Sensitivity": "sound_sensitivity",
  };

  const score =
    answers?.above13age_bvdq_scores ??
    answers?.["4to8age_bvdq_scores"] ??
    answers?.["9to13age_bvdq_scores"] ??
    "--";

  const adultMaxScore = 75;
  const preTeenMaxScore = 48;
  const childMaxScore = 13;

  const adultPointerPosition =
    score !== "--" && score >= 0 && score <= adultMaxScore
      ? (score / adultMaxScore) * 100
      : 0;

  const preteenPointerPosition =
    score !== "--" && score >= 0 && score <= preTeenMaxScore
      ? (score / preTeenMaxScore) * 100
      : 0;

  const childPointerPosition =
    score !== "--" && score >= 0 && score <= childMaxScore
      ? (score / childMaxScore) * 100
      : 0;

  // const preferredContactMethod = answers?.preferred_contact_method || [];
  // console.log("selecte lead . formsort response ", decryptData(selectedLead?.formsortResponse))
  const ageCategory = decryptData(selectedLead?.formsortResponse)?.answers
    ?.age || decryptData(selectedLead?.formsortResponse)?.answers?.ageCategory;
  return (
    <div className="tableDesigned">
      <Box>
        <Paper>
          <EnhancedTableToolbar numSelected={selected.length} />
          <TableContainer>
            <Table>
              <EnhancedTableHead
                numSelected={selected.length}
                order={order}
                orderBy={orderBy}
                onSelectAllClick={handleSelectAllClick}
                onRequestSort={handleRequestSort}
                rowCount={totalLeads}
              />
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      {/* You can replace this with a spinner or loader component */}
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: '350px', // or adjust as per the space where rows usually appear
                          width: '100%',
                          padding: '16px',
                          boxSizing: 'border-box',
                        }}
                      >
                        <h4 style={{ margin: 0 }}>Loading...</h4>
                      </div>


                    </TableCell>
                  </TableRow>
                ) : rows.length > 0 ? (
                  rows.map((row, index) => {
                    const isItemSelected = isSelected(row._id);
                    const labelId = `enhanced-table-checkbox-${index}`;

                    return (
                      <TableRow
                        hover
                        role="checkbox"
                        aria-checked={isItemSelected}
                        tabIndex={-1}
                        key={row._id}
                        selected={isItemSelected}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            onClick={(event) => handleClick(event, row._id)}
                            color="primary"
                            checked={isItemSelected}
                            inputProps={{
                              "aria-labelledby": labelId,
                            }}
                          />
                        </TableCell>
                        <TableCell
                          component="th"
                          id={labelId}
                          scope="row"
                          padding="none"
                          onClick={() => handleClickOpenedLeadModal(row?._id)}
                        >
                          {convertToUSFormat(row?.created_at)}
                        </TableCell>
                        <TableCell
                          onClick={() => handleClickOpenedLeadModal(row?._id)}
                        >

                          {decryptData(row?.formsortResponse)?.answers?.Enter_firstname ||
                            decryptData(row?.formsortResponse)?.answers?.Enter_firstname2 ||
                            decryptData(row?.formsortResponse)?.answers?.name ||
                            ''}{" "}
                             {decryptData(row?.formsortResponse)?.answers?.Enter_lastname ||
                            decryptData(row?.formsortResponse)?.answers?.Enter_lastname ||
                            decryptData(row?.formsortResponse)?.answers?.Enter_lastname ||
                            ''}
                        </TableCell>
                        <TableCell
                          onClick={() => handleClickOpenedLeadModal(row?._id)}
                        >
                          {decryptData(row?.formsortResponse)?.answers?.age ||
                            decryptData(row?.formsortResponse)?.answers?.Enter_currentAge || "--"}
                        </TableCell>
                        <TableCell
                          onClick={() => handleClickOpenedLeadModal(row?._id)}
                        >
                          {decryptData(row?.formsortResponse)?.answers
                            ?.phone_number || decryptData(row?.formsortResponse)?.answers
                              ?.nbsp || "--"}
                        </TableCell>
                        <TableCell
                          onClick={() => handleClickOpenedLeadModal(row?._id)}
                        >
                          {/* {row?.above13age_bvdq_scores ?? row?.["4to8age_bvdq_scores"] ?? row?.["9to13age_bvdq_scores"] ?? row?.["9to14age_bvdq_scores"] ?? "--"} */}
                          {row?.bvdqScores}
                        </TableCell>
                        {/* <TableCell
                          onClick={() => handleClickOpenedLeadModal(row?._id)}
                        >
                          {decryptData(row?.formsortResponse)?.answers
                            ?.country_of_residence || decryptData(row?.formsortResponse)?.answers
                              ?.country_of_residence_ || "--"}
                        </TableCell> */}
                         <TableCell
                          onClick={() => handleClickOpenedLeadModal(row?._id)}
                        >
                          {decryptData(row?.formsortResponse)?.answers
                            ?.state || decryptData(row?.formsortResponse)?.answers
                              ?.state || "--"}
                        </TableCell>
                        <TableCell
                          onClick={() => handleClickOpenedLeadModal(row?._id)}
                        >
                          {decryptData(row?.formsortResponse)?.answers
                            ?.city || decryptData(row?.formsortResponse)?.answers
                              ?.city || "--"}
                        </TableCell>
                        <TableCell>{row?.providerId?.organizationName}</TableCell>
                        <TableCell>
                          <FormControl sx={{ m: 1, minWidth: 120 }}>
                            <Select
                              className={`statelabeld ${getclassName(
                                row.status
                              )}`}
                              value={row.status}
                              onChange={(event) => handleStatusChange(event, row)}
                              displayEmpty
                              inputProps={{ "aria-label": "Without label" }}
                              size="small"
                            >
                              <MenuItem value="New Lead">New Lead</MenuItem>
                              <MenuItem value="Scheduled For Contact">
                                Scheduled For Contact
                              </MenuItem>
                              <MenuItem value="Contact Attempted">
                                Contact Attempted
                              </MenuItem>
                              <MenuItem value="Ready To Schedule">
                                Ready To Schedule
                              </MenuItem>
                              <MenuItem value="In Process">In Process</MenuItem>
                              <MenuItem value="Scheduled">Scheduled</MenuItem>
                              <MenuItem value="UnScheduled">Unscheduled</MenuItem>
                              <MenuItem value="Archived">Archived</MenuItem>

                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <div className="btn-groups">
                            <Tooltip title="View" arrow>
                              <button
                                onClick={() => handleClickOpenedLeadModal(row?._id)}
                                className="actionbuttons"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                >
                                  <path
                                    d="M20.4908 11.1432C20.8361 11.6696 20.8361 12.3304 20.4908 12.8568C19.4032 14.5146 16.5729 18 11.9999 18C7.4269 18 4.5966 14.5146 3.50898 12.8568C3.16367 12.3304 3.16367 11.6696 3.50898 11.1432C4.5966 9.48535 7.4269 6 11.9999 6C16.5729 6 19.4032 9.48535 20.4908 11.1432Z"
                                    stroke="#35353A"
                                    strokeWidth="1.25"
                                    strokeLinecap="round"
                                  />
                                  <path
                                    d="M15 12C15 13.6569 13.6568 15 12 15C10.3431 15 9 13.6569 9 12C9 10.3431 10.3431 9 12 9C13.6568 9 15 10.3431 15 12Z"
                                    stroke="#35353A"
                                    strokeWidth="1.25"
                                    strokeLinecap="round"
                                  />
                                </svg>
                              </button>
                            </Tooltip>
                            <Tooltip title="Add Notes" arrow>
                              <button
                                onClick={() => handleClickOpen(row?._id)}
                                className="actionbuttons"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                >
                                  <path
                                    d="M18 10V9.82843C18 9.29799 17.7893 8.78929 17.4142 8.41421L13.5858 4.58579C13.2107 4.21071 12.702 4 12.1716 4H12M18 10V18C18 19.1046 17.1046 20 16 20H13M18 10H14C12.8954 10 12 9.10457 12 8V4M12 4H8C6.89543 4 6 4.89543 6 6V13M6 20V17.5M6 20V22.5M6 20H3.5M6 20H8.5"
                                    stroke="#35353A"
                                    strokeWidth="1.25"
                                    strokeLinecap="round"
                                  />
                                </svg>
                              </button>
                            </Tooltip>
                            <Tooltip title="Patient Id" arrow>
                              <button
                                onClick={() => handleClickOpenPatientId(row._id)}
                                className="actionbuttons"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                >
                                  <path
                                    d="M16.5 3.5H18.5C19.6046 3.5 20.5 4.39543 20.5 5.5V7.5M16.5 20.5H18.5C19.6046 20.5 20.5 19.6046 20.5 18.5V16.5M7.5 3.5H5.5C4.39543 3.5 3.5 4.39543 3.5 5.5V7.5M7.5 20.5H5.5C4.39543 20.5 3.5 19.6046 3.5 18.5V16.5M7.5 17.5H9.5C10.0523 17.5 10.5 17.0523 10.5 16.5V14.5C10.5 13.9477 10.0523 13.5 9.5 13.5H7.5C6.94772 13.5 6.5 13.9477 6.5 14.5V16.5C6.5 17.0523 6.94772 17.5 7.5 17.5ZM14.5 14C14.5 14.2761 14.2761 14.5 14 14.5C13.7239 14.5 13.5 14.2761 13.5 14C13.5 13.7239 13.7239 13.5 14 13.5C14.2761 13.5 14.5 13.7239 14.5 14ZM14.5 17C14.5 17.2761 14.2761 17.5 14 17.5C13.7239 17.5 13.5 17.2761 13.5 17C13.5 16.7239 13.7239 16.5 14 16.5C14.2761 16.5 14.5 16.7239 14.5 17ZM17.5 17C17.5 17.2761 17.2761 17.5 17 17.5C16.7239 17.5 16.5 17.2761 16.5 17C16.5 16.7239 16.7239 16.5 17 16.5C17.2761 16.5 17.5 16.7239 17.5 17ZM17.5 14C17.5 14.2761 17.2761 14.5 17 14.5C16.7239 14.5 16.5 14.2761 16.5 14C16.5 13.7239 16.7239 13.5 17 13.5C17.2761 13.5 17.5 13.7239 17.5 14ZM7.5 10.5H9.5C10.0523 10.5 10.5 10.0523 10.5 9.5V7.5C10.5 6.94772 10.0523 6.5 9.5 6.5H7.5C6.94772 6.5 6.5 6.94772 6.5 7.5V9.5C6.5 10.0523 6.94772 10.5 7.5 10.5ZM14.5 10.5H16.5C17.0523 10.5 17.5 10.0523 17.5 9.5V7.5C17.5 6.94772 17.0523 6.5 16.5 6.5H14.5C13.9477 6.5 13.5 6.94772 13.5 7.5V9.5C13.5 10.0523 13.9477 10.5 14.5 10.5Z"
                                    stroke="#35353A"
                                    strokeWidth="1.25"
                                    strokeLinecap="round"
                                  />
                                </svg>
                              </button>
                            </Tooltip>
                            <Tooltip title="Change Provider" arrow>
                              <button
                                onClick={() => handleClickChangeProvider(row)}
                                className="actionbuttons"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                >
                                  <path
                                    d="M12.0595 18.7885L14.6885 16.175L12.0595 13.5615L11.2213 14.4L12.45 15.6288C11.8295 15.6391 11.2583 15.5561 10.7365 15.3798C10.2147 15.2036 9.77558 14.9373 9.41925 14.5808C9.06658 14.2283 8.79958 13.8257 8.61825 13.373C8.43692 12.9205 8.34625 12.468 8.34625 12.0155C8.34625 11.7322 8.3805 11.4488 8.449 11.1655C8.51767 10.8822 8.61542 10.6103 8.74225 10.35L7.85375 9.5C7.61542 9.88467 7.43917 10.2882 7.325 10.7105C7.21083 11.1328 7.15375 11.5627 7.15375 12C7.15375 12.6138 7.274 13.2195 7.5145 13.817C7.75483 14.4147 8.10867 14.9488 8.576 15.4193C9.04333 15.8898 9.61067 16.241 10.278 16.473C10.9452 16.7052 11.6308 16.8263 12.3348 16.8365L11.2213 17.95L12.0595 18.7885ZM16.1557 14.5C16.3942 14.1153 16.5705 13.7118 16.6845 13.2895C16.7987 12.8672 16.8558 12.4373 16.8558 12C16.8558 11.3873 16.7381 10.7786 16.5027 10.1738C16.2676 9.56908 15.9148 9.033 15.4443 8.5655C14.9801 8.095 14.4112 7.74625 13.7375 7.51925C13.0638 7.29225 12.3763 7.17875 11.675 7.17875L12.7885 6.05L11.95 5.2115L9.32125 7.825L11.95 10.4385L12.7885 9.6L11.5442 8.35575C12.1544 8.35575 12.7282 8.44642 13.2655 8.62775C13.8027 8.80925 14.2452 9.07425 14.5932 9.42275C14.9412 9.77142 15.2067 10.1723 15.3895 10.6253C15.5722 11.0784 15.6635 11.5315 15.6635 11.9845C15.6635 12.2678 15.6292 12.5512 15.5605 12.8345C15.492 13.1178 15.3943 13.3897 15.2673 13.65L16.1557 14.5ZM12.0017 21.5C10.6877 21.5 9.45267 21.2507 8.2965 20.752C7.14033 20.2533 6.13467 19.5766 5.2795 18.7218C4.42433 17.8669 3.74725 16.8617 3.24825 15.706C2.74942 14.5503 2.5 13.3156 2.5 12.0017C2.5 10.6877 2.74933 9.45267 3.248 8.2965C3.74667 7.14033 4.42342 6.13467 5.27825 5.2795C6.13308 4.42433 7.13833 3.74725 8.294 3.24825C9.44967 2.74942 10.6844 2.5 11.9983 2.5C13.3123 2.5 14.5473 2.74933 15.7035 3.248C16.8597 3.74667 17.8653 4.42342 18.7205 5.27825C19.5757 6.13308 20.2528 7.13833 20.7518 8.294C21.2506 9.44967 21.5 10.6844 21.5 11.9983C21.5 13.3123 21.2507 14.5473 20.752 15.7035C20.2533 16.8597 19.5766 17.8653 18.7218 18.7205C17.8669 19.5757 16.8617 20.2528 15.706 20.7518C14.5503 21.2506 13.3156 21.5 12.0017 21.5ZM12 20C14.2333 20 16.125 19.225 17.675 17.675C19.225 16.125 20 14.2333 20 12C20 9.76667 19.225 7.875 17.675 6.325C16.125 4.775 14.2333 4 12 4C9.76667 4 7.875 4.775 6.325 6.325C4.775 7.875 4 9.76667 4 12C4 14.2333 4.775 16.125 6.325 17.675C7.875 19.225 9.76667 20 12 20Z"
                                    stroke="#35353A"
                                    strokeWidth="1.25"
                                    strokeLinecap="round"
                                  />
                                </svg>
                              </button>
                            </Tooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={10} className="norecord" align="center">
                      No Record Found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>

            </Table>
          </TableContainer>

          {
            !loading &&
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              component="div"
              count={totalLeads}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          }

        </Paper>
      </Box>
      <PatientIdModal
        openPatientId={openPatientId}
        handleClose={handleClickPatientIdClose}
        lead={selectedLead}
      />
      <AddNotesModal
        open={open}
        handleClose={handleClose}
        leadId={selectedLeadId}
      />
      <ChangeProviderModal
        changeProvider={changeProvider}
        handleClose={handleClose}
        lead={selectedLead}
        setUpdate={setUpdate}
        type="lead"
      />

      <Dialog
        onClose={handleClosedLeadModal}
        aria-labelledby="customized-dialog-title"
        open={openDialouge}
        className="weighty"
      >
        <div className="d-flex align-items-center justify-content-between">
          <DialogTitle id="customized-dialog-title">
            Patient Profile
          </DialogTitle>
          <div className="d-flex align-items-center">
            <IconButton aria-label="close" onClick={handleClosedLeadModal}>
              <CloseIcon />
            </IconButton>
          </div>
        </div>

        <DialogContent dividers className="modelcontentSection">
          <div className="row" ref={dialogRef}>
            <div className="col-lg-4 col-sm-12 border-side">
              <ContactInfoForm
                selectedLead={selectedLead}
                getclassName={getclassName}
                handleStatusUpdate={handleStatusUpdate}
                handleCopy={handleCopy}
              />

              <Notes
                leadId={selectedLead?._id}
                setOpen={setOpen}
                updatedNotes={updatedNotes}
              />
            </div>
            <div className="col-lg-8 col-sm-12 ">
              <div className="row mb-3">
                <div className="col-lg-3 col-sm-12">
                  <div className="blockdig new-blockdig-controller">
                    <h6>
                      {getAgeCategory(ageCategory)}
                    </h6>
                    <p>{formatDate(selectedLead?.created_at)}</p>
                  </div>
                </div>
                <div className="col-lg-5 col-sm-12">
                  <div className="blockdig new-blockdig-controller">
                    <h6>
                      BVDQ™ Score of <span className="blued">{score}</span>
                    </h6>

                    {answers?.above13age_bvdq_scores !== undefined &&
                    answers?.above13age_bvdq_scores !== null ? (
                      <div className="custom-slider-container">
                        <div className="custom-slider-bar" ref={sliderBarRef}>
                          <div
                            className="custom-slider-segment custom-unlikely"
                            style={{
                              left: 0,
                              width: `calc((14 / 75) * 100%)`,
                            }}
                          >
                            <div className="custom-slider-label mt-5 custom-label-unlikely">
                              Unlikely
                              <br />
                              <span>0-14</span>
                            </div>
                          </div>
                          <div
                            className="custom-slider-segment custom-mild"
                            style={{
                              left: `calc((14 / 75) * 100%)`,
                              width: `calc((16 / 75) * 100%)`,
                            }}
                          >
                            <div className="custom-slider-label mt-5 custom-label-mild">
                              Moderate
                              <br />
                              <span>15-30</span>
                            </div>
                          </div>
                          <div
                            className="custom-slider-segment custom-moderate"
                            style={{
                              left: `calc((30 / 75) * 100%)`,
                              width: `calc((15 / 75) * 100%)`,
                            }}
                          >
                            <div className="custom-slider-label mt-5 custom-label-moderate">
                              High
                              <br />
                              <span>31-45</span>
                            </div>
                          </div>
                          <div
                            className="custom-slider-segment custom-severe"
                            style={{
                              left: `calc((45 / 75) * 100%)`,
                              width: `calc((30 / 75) * 100%)`,
                            }}
                          >
                            <div className="custom-slider-label mt-5 custom-label-severe">
                              Severe
                              <br />
                              <span>46-75</span>
                            </div>
                          </div>
                          <div
                            className="custom-slider-pointer"
                            ref={pointerRef}
                            style={{
                              left: `calc(${adultPointerPosition}% - 10px)`,
                            }}
                          ></div>
                        </div>
                      </div>
                    ) : answers?.["4to8age_bvdq_scores"] !== undefined &&
                    answers?.["4to8age_bvdq_scores"] !== null ? (
                      <div className="custom-slider-container">
                        <div className="custom-slider-bar" ref={sliderBarRef}>
                          <div
                            className="custom-slider-segment custom-unlikely"
                            style={{
                              left: 0,
                              width: `calc((3 / 13) * 100%)`,
                            }}
                          >
                            <div className="custom-slider-label mt-5 custom-label-unlikely">
                              Unlikely
                              <br />
                              <span>0-3</span>
                            </div>
                          </div>
                          <div
                            className="custom-slider-segment custom-mild"
                            style={{
                              left: `calc((3 / 13) * 100%)`,
                              width: `calc((4 / 13) * 100%)`,
                            }}
                          >
                            <div className="custom-slider-label mt-5 custom-label-mild">
                              Moderate
                              <br />
                              <span>4-7</span>
                            </div>
                          </div>
                          <div
                            className="custom-slider-segment custom-moderate"
                            style={{
                              left: `calc((7 / 13) * 100%)`,
                              width: `calc((3 / 13) * 100%)`,
                            }}
                          >
                            <div className="custom-slider-label mt-5 custom-label-moderate">
                              High
                              <br />
                              <span>8-10</span>
                            </div>
                          </div>
                          <div
                            className="custom-slider-segment custom-severe"
                            style={{
                              left: `calc((10 / 13) * 100%)`,
                              width: `calc((3 / 13) * 100%)`,
                            }}
                          >
                            <div className="custom-slider-label mt-5 custom-label-severe">
                              Severe
                              <br />
                              <span>11-13</span>
                            </div>
                          </div>
                          <div
                            className="custom-slider-pointer"
                            ref={pointerRef}
                            style={{
                              left: `calc(${childPointerPosition}% - 10px)`,
                            }}
                          ></div>
                        </div>
                      </div>
                    ) : answers?.["9to13age_bvdq_scores"] !== undefined &&
                    answers?.["9to13age_bvdq_scores"] !== null ? (
                      <div className="custom-slider-container">
                        <div className="custom-slider-bar" ref={sliderBarRef}>
                          <div
                            className="custom-slider-segment custom-unlikely"
                            style={{
                              left: 0,
                              width: `calc((9 / 48) * 100%)`,
                            }}
                          >
                            <div className="custom-slider-label mt-5 custom-label-unlikely">
                              Unlikely
                              <br />
                              <span>0-9</span>
                            </div>
                          </div>
                          <div
                            className="custom-slider-segment custom-mild"
                            style={{
                              left: `calc((9 / 48) * 100%)`,
                              width: `calc((10 / 48) * 100%)`,
                            }}
                          >
                            <div className="custom-slider-label mt-5 custom-label-mild">
                              Moderate
                              <br />
                              <span>10-19</span>
                            </div>
                          </div>
                          <div
                            className="custom-slider-segment custom-moderate"
                            style={{
                              left: `calc((19 / 48) * 100%)`,
                              width: `calc((10 / 48) * 100%)`,
                            }}
                          >
                            <div className="custom-slider-label mt-5 custom-label-moderate">
                              High
                              <br />
                              <span>20-29</span>
                            </div>
                          </div>
                          <div
                            className="custom-slider-segment custom-severe"
                            style={{
                              left: `calc((29 / 48) * 100%)`,
                              width: `calc((19 / 48) * 100%)`,
                            }}
                          >
                            <div className="custom-slider-label mt-5 custom-label-severe">
                              Severe
                              <br />
                              <span>30-48</span>
                            </div>
                          </div>
                          <div
                            className="custom-slider-pointer"
                            ref={pointerRef}
                            style={{
                              left: `calc(${preteenPointerPosition}% - 10px)`,
                            }}
                          ></div>
                        </div>
                      </div>
                    ) : (
                      <div>No Scale available</div>
                    )}
                  </div>
                </div>
                <div className="col-lg-4 col-sm-12">
                  <div className="blockdig new-blockdig-controller">
                    <h6>
                      SSI Score of{" "}
                      <span className="reds">
                        {
                          decryptData(selectedLead?.formsortResponse)?.answers
                            ?.ssi_scores
                        }
                      </span>
                    </h6>
                    <p>Top 3 Symptoms by Severity</p>
                    <div className="symptonlist">
                      {topSymptoms.map((symptom, index) => (
                        <div key={index} className="chip">
                          {symptom.name}: {symptom.value}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-lg-8 col-sm-12">
                  <div className="scoreborad greybackbox">
                    <h5>
                      Symptom Frequency (BVDQ Score:{" "}
                      {answers?.above13age_bvdq_scores ??
                        answers?.["4to8age_bvdq_scores"] ??
                        answers?.["9to13age_bvdq_scores"] ??
                        "--"}
                      )
                    </h5>

                    {answers?.above13age_bvdq_scores !== undefined &&
                    answers?.above13age_bvdq_scores !== null ? (
                      <div className="questionblockw">
                        {above13AgeQuestionsBVDQ.map((item, index) => (
                          <div
                            key={index}
                            className={`d-flex align-items-center justify-content-between ${answers?.[item.key] === "Always"
                              ? "hightedquestion"
                              : ""
                              }`}
                          >
                            {item.question} <span className="question"></span>
                            <span className="reason">
                              {answers?.[item.key]}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : answers?.["4to8age_bvdq_scores"] !== undefined &&
                    answers?.["4to8age_bvdq_scores"] !== null ? (
                      <div className="questionblockw">
                        {age4to8QuestionsBVDQ.map((item, index) => (
                          <div
                            key={index}
                            className={`d-flex align-items-center justify-content-between ${answers?.[item.key] === "Yes"
                              ? "hightedquestion"
                              : ""
                              }`}
                          >
                            {item.question} <span className="question"></span>
                            <span className="reason">
                              {answers?.[item.key]}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : answers?.["9to13age_bvdq_scores"] !== undefined &&
                    answers?.["9to13age_bvdq_scores"] !== null ? (
                      <div className="questionblockw">
                        {age9to13QuestionsBVDQ.map((item, index) => (
                          <div
                            key={index}
                            className={`d-flex align-items-center justify-content-between ${answers?.[item.key] === "Always"
                              ? "hightedquestion"
                              : ""
                              }`}
                          >
                            {item.question} <span className="question"></span>
                            <span className="reason">
                              {answers?.[item.key]}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div>No questions available</div>
                    )}
                  </div>

                  <div className="greybackbox">
                    <h5>Additional Comments</h5>
                    <p className="eplippsewrap-full">
                      {answers?.comment_section_optional || answers?.comment_section_optional_youth
                        ? <>
                          {answers?.comment_section_optional && (
                            <div>{answers.comment_section_optional}</div>
                          )}
                          {answers?.comment_section_optional_youth && (
                            <div>{answers.comment_section_optional_youth}</div>
                          )}
                        </>
                        : "No Comments Available"}
                    </p>
                  </div>
                </div>
                <div className="col-lg-4 col-sm-12">
                  <div className="greybackbox">
                    <h5>
                      Level of Discomfort (SSI:{" "}
                      {
                        decryptData(selectedLead?.formsortResponse)?.answers
                          ?.ssi_scores
                      }
                      )
                    </h5>
                    {Object.keys(symptomKeyMap).map((displayName) => {
                      const answerKey = symptomKeyMap[displayName];
                      if (answers && answers[answerKey] !== undefined) {
                        const isTopSymptom =
                          topSymptomNames.includes(displayName);
                        return (
                          <div
                            key={answerKey}
                            className={`smallitems d-flex align-items-center justify-content-between ${isTopSymptom ? "highlighted" : ""
                              }`}
                          >
                            <span>{displayName}:</span>
                            <span>{answers[answerKey]}</span>
                          </div>
                        );
                      }

                      return null;
                    })}
                  </div>
                  <div className="greybackbox">
                    <h5>Health History</h5>
                    {answers?.above13age_bvdq_scores !== undefined &&
                    answers?.above13age_bvdq_scores !== null ? (
                      <>
                        {above13AgeQuestionsHistory.map((item, index) =>
                          answers?.[item.key] === "Yes" ? (
                            <strong key={index}>
                              <div className="d-flex align-items-center font-small justify-content-between">
                                <span className="nowrap-custom">
                                  {item.question}
                                </span>
                                <span>{answers?.[item.key]}</span>
                              </div>
                            </strong>
                          ) : (
                            <div
                              key={index}
                              className="d-flex align-items-center font-small justify-content-between"
                            >
                              <span className="nowrap-custom">
                                {item.question}
                              </span>
                              <span>{answers?.[item.key]}</span>
                            </div>
                          )
                        )}
                      </>
                    ) : answers?.["4to8age_bvdq_scores"] !== undefined &&
                    answers?.["4to8age_bvdq_scores"] !== null ? (
                      <>
                        {age4to8QuestionsHistory.map((item, index) =>
                          answers?.[item.key] === "Yes" ? (
                            <strong key={index}>
                              <div className="d-flex align-items-center font-small justify-content-between">
                                <span className="nowrap-custom">
                                  {item.question}
                                </span>
                                <span>{answers?.[item.key]}</span>
                              </div>
                            </strong>
                          ) : (
                            <div
                              key={index}
                              className="d-flex align-items-center font-small justify-content-between"
                            >
                              <span className="nowrap-custom">
                                {item.question}
                              </span>
                              <span>{answers?.[item.key]}</span>
                            </div>
                          )
                        )}
                      </>
                    ) : answers?.["9to13age_bvdq_scores"] !== undefined &&
                    answers?.["9to13age_bvdq_scores"] !== null ? (
                      <>
                        {age9to13QuestionsHistory.map((item, index) =>
                          answers?.[item.key] === "Yes" ? (
                            <strong key={index}>
                              <div className="d-flex align-items-center font-small justify-content-between">
                                <span className="nowrap-custom">
                                  {item.question}
                                </span>
                                <span>{answers?.[item.key]}</span>
                              </div>
                            </strong>
                          ) : (
                            <div
                              key={index}
                              className="d-flex align-items-center font-small justify-content-between"
                            >
                              <span className="nowrap-custom">
                                {item.question}
                              </span>
                              <span>{answers?.[item.key]}</span>
                            </div>
                          )
                        )}
                      </>
                    ) : (
                      <div>No health history questions available</div>
                    )}
                  </div>
                  <div className="greybackbox">
                    <h5>Feedback on the BVDQ™ Assessment</h5>
                    <span>Discovered BVDQ™ Assessment via :</span>
                    <p className="eplippsewrap">
                      {answers?.find_bvdq_assessment
                        ? {
                          internet_search: "Internet Search",
                          facebook: "Facebook",
                          instagram: "Instagram",
                          tiktok: "TikTok",
                          forum_or_blog: "Forum or Blog",
                          referred_by_a_friend: "Referred by a Friend",
                          referred_by_a_professional: "Referred by a Professional",
                          youtube: "YouTube",
                        }[answers.find_bvdq_assessment] || answers.find_bvdq_assessment
                        : "No Feedback Available"}
                    </p>

                    <span>Additional Feedback :</span>
                    <p className="eplippsewrap">
                      {answers?.source_of_discovery
                        ? answers.source_of_discovery
                        : "No Feedback Available"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-lg-12 col-sm-12">
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
